/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next'
import { FormikTouched, FormikErrors, FormikHandlers, getIn } from 'formik'
import { InputAdornment, Typography } from '@material-ui/core'

import { DifficultySlider } from 'components/data/DifficultySlider/DifficultySlider'
import { MIN_CONTENT_DURATION } from 'constants/knowledgeBaseContent'
import { RestrictedContent } from 'components/auth/RestrictedContent/RestrictedContent'
import { ADMIN_ROLE } from 'constants/roleGroups'
import { KnowledgeBaseContentFormFields, PresentationTestSubForm } from '../types'
import * as Styled from '../KnowledgeBaseContentForm.styles'

interface PresentationTestFormFieldsProps {
  onChange: FormikHandlers['handleChange']
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  onBlur: FormikHandlers['handleBlur']
  values?: Partial<KnowledgeBaseContentFormFields>
  touched: Partial<FormikTouched<KnowledgeBaseContentFormFields>>
  errors: Partial<FormikErrors<KnowledgeBaseContentFormFields>>
  contentValues: PresentationTestSubForm
  contentTouched?: FormikTouched<PresentationTestSubForm>
  contentErrors?: FormikErrors<PresentationTestSubForm>
}

const PresentationTestFormFields = ({
  onChange: handleChange,
  onBlur: handleBlur,
  setFieldValue,
  values,
  touched,
  errors,
  contentValues,
  contentTouched,
  contentErrors,
}: PresentationTestFormFieldsProps) => {
  const { t } = useTranslation()

  const handleDifficultyChange = (
    _event: any,
    newValue: number | number[]
  ) => {
    setFieldValue('subForm.difficulty', newValue)
  }

  return (
    <>
      <Styled.InfoSection
        label={t('knowledgeBase.difficulty')}
        editValueComponent={
          <Styled.DifficultySliderContainer>
            <DifficultySlider
              name='subForm.difficulty'
              value={contentValues.difficulty}
              onChange={handleDifficultyChange as any}
              track={false}
            />
          </Styled.DifficultySliderContainer>
        }
      />
      <Styled.InfoSection
        label={t('knowledgeBase.duration')}
        editValueComponent={
          <Styled.TextField
            id='duration'
            name='duration'
            type='number'
            value={values?.duration}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.duration}
            errorMessage={t(errors.duration || '')}
            InputProps={{
              inputProps: {
                min: MIN_CONTENT_DURATION,
              },
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position='start'>
                  {t('common.minutes')}
                </InputAdornment>
              )
            }}
          />
        }
      />
      <RestrictedContent accessRoles={[ADMIN_ROLE]}>
        <Styled.MetadataHeader>
          <Typography variant='h3'>
            {t('knowledgeBase.metadata')}
          </Typography>
        </Styled.MetadataHeader>
        {contentValues.metadata?.map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Styled.MetadataRow key={index}>
            <Styled.InfoSection
              label={t('knowledgeBase.key')}
              editValueComponent={
                <Styled.TextField
                  id={`subForm.metadata[${index}].key`}
                  name={`subForm.metadata[${index}].key`}
                  value={contentValues.metadata?.[index].key}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={getIn(contentTouched, `metadata[${index}].key`)}
                  errorMessage={t(getIn(contentErrors, `metadata[${index}].key`) || '')}
                />
              }
            />
            <Styled.InfoSection
              label={t('knowledgeBase.value')}
              editValueComponent={
                <Styled.TextField
                  id={`subForm.metadata[${index}].value`}
                  name={`subForm.metadata[${index}].value`}
                  value={contentValues.metadata?.[index].value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={getIn(contentTouched, `metadata[${index}].value`)}
                  errorMessage={t(getIn(contentErrors, `metadata[${index}].value`) || '')}
                />
              }
            />
            <Styled.CheckboxInfoSection
              label={t('knowledgeBase.isJSON')}
              editValueComponent={
                <Styled.Checkbox
                  id={`subForm.metadata[${index}].isJSON`}
                  name={`subForm.metadata[${index}].isJSON`}
                  label=''
                  labelFontSize='18px'
                  labelFontWeight='bold'
                  checked={contentValues.metadata?.[index].isJSON}
                  value={contentValues.metadata?.[index].isJSON}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={getIn(contentTouched, `metadata[${index}].isJSON`)}
                  errorMessage={t(getIn(contentErrors, `metadata[${index}].isJSON`) || '')}
                />
              }
            />
            <Styled.CheckboxInfoSection
              label=' '
              editValueComponent={
                <Styled.RemoveMetadataButton
                  onClick={() =>
                    setFieldValue(
                      'subForm.metadata',
                      [...(contentValues.metadata ?? []).filter((v, idx) => index !== idx)]
                    )}
                />
              }
            />
          </Styled.MetadataRow>
        ))}
        <Styled.AddMetadataButton
          onClick={() =>
            setFieldValue(
              'subForm.metadata',
              [...(contentValues.metadata ?? []), { key: '', value: '', isJSON: false }]
            )}
        />
      </RestrictedContent>
    </>
  )
}

export {
  PresentationTestFormFields,
}
